import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { showWizardToast } from "../wizardFeedback";
import useDragAutoScroll from "../../../Cms/MenuTree/useDragAutoScroll";
import { formatDualMoney } from "../../../../../utils/currency";
import {
  getPrimaryMenuLanguage,
  getProductDisplayTitle,
  getCategoryDisplayTitle,
  getSubcategoryDisplayTitle,
} from "./utils";

function collectAssignedProductIds(relations) {
  const assigned = new Set();
  Object.values(relations?.categoryProducts || {}).forEach((ids) => {
    (ids || []).forEach((id) => assigned.add(id));
  });
  Object.values(relations?.subcategoryProducts || {}).forEach((ids) => {
    (ids || []).forEach((id) => assigned.add(id));
  });
  return assigned;
}

function removeProductFromAllMaps(productId, categoryProducts, subcategoryProducts) {
  const nextCategoryProducts = { ...categoryProducts };
  const nextSubcategoryProducts = { ...subcategoryProducts };

  Object.keys(nextCategoryProducts).forEach((key) => {
    nextCategoryProducts[key] = (nextCategoryProducts[key] || []).filter((id) => id !== productId);
  });
  Object.keys(nextSubcategoryProducts).forEach((key) => {
    nextSubcategoryProducts[key] = (nextSubcategoryProducts[key] || []).filter((id) => id !== productId);
  });

  return { nextCategoryProducts, nextSubcategoryProducts };
}

function getPayloadProductIds(e) {
  try {
    const raw = e.dataTransfer.getData("application/json");
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed?.productIds) && parsed.productIds.length) {
      return parsed.productIds;
    }
    if (parsed?.productId) return [parsed.productId];
  } catch {
    // ignore
  }
  return [];
}

const Step4LinkProducts = ({ draft, setDraft, goNext, goPrev, productPhotos = {} }) => {
  const { t } = useTranslation();
  const menuPrimaryLanguage = getPrimaryMenuLanguage(draft?.menu);
  const products = Array.isArray(draft?.products) ? draft.products : [];
  const relations = draft?.relations || {};

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [draggingIds, setDraggingIds] = useState([]);
  const [dropHighlight, setDropHighlight] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const dragMovedRef = useRef(false);
  const autoScroll = useDragAutoScroll({ moveEvent: "dragover" });

  const categoriesOrdered = useMemo(() => {
    const order = Array.isArray(relations.menuCategories) ? relations.menuCategories : [];
    const byId = new Map((draft.categories || []).map((c) => [c.tempId, c]));
    return order.map((id) => byId.get(id)).filter(Boolean);
  }, [draft.categories, relations.menuCategories]);

  const categoryIdsKey = categoriesOrdered.map((c) => c.tempId).join("|");

  useEffect(() => {
    setExpandedCategories((prev) => {
      const next = { ...prev };
      categoriesOrdered.forEach((cat) => {
        if (next[cat.tempId] === undefined) {
          next[cat.tempId] = false;
        }
      });
      return next;
    });
  }, [categoryIdsKey, categoriesOrdered]);

  const assignedIds = useMemo(() => collectAssignedProductIds(relations), [relations]);

  const unassignedProducts = useMemo(
    () => products.filter((p) => !assignedIds.has(p.tempId)),
    [products, assignedIds]
  );

  const getSubcategoriesForCategory = (categoryTempId) => {
    const order = relations.categorySubcategories?.[categoryTempId] || [];
    const byId = new Map((draft.subcategories || []).map((s) => [s.tempId, s]));
    return order.map((id) => byId.get(id)).filter(Boolean);
  };

  const countProductsInCategory = (categoryTempId) => {
    const direct = (relations.categoryProducts?.[categoryTempId] || []).length;
    const subs = getSubcategoriesForCategory(categoryTempId);
    const inSubs = subs.reduce(
      (sum, sub) => sum + (relations.subcategoryProducts?.[sub.tempId] || []).length,
      0
    );
    return direct + inSubs;
  };

  const assignProductsBatch = (productIds, targetType, targetId) => {
    const uniqueIds = [...new Set((productIds || []).filter(Boolean))];
    if (!uniqueIds.length || !targetId) return;

    setDraft((prev) => {
      const rel = prev.relations || {};
      let nextCategoryProducts = { ...(rel.categoryProducts || {}) };
      let nextSubcategoryProducts = { ...(rel.subcategoryProducts || {}) };

      uniqueIds.forEach((productId) => {
        const cleaned = removeProductFromAllMaps(
          productId,
          nextCategoryProducts,
          nextSubcategoryProducts
        );
        nextCategoryProducts = cleaned.nextCategoryProducts;
        nextSubcategoryProducts = cleaned.nextSubcategoryProducts;

        if (targetType === "category") {
          nextCategoryProducts[targetId] = [...(nextCategoryProducts[targetId] || []), productId];
        } else {
          nextSubcategoryProducts[targetId] = [
            ...(nextSubcategoryProducts[targetId] || []),
            productId,
          ];
        }
      });

      return {
        ...prev,
        relations: {
          ...rel,
          categoryProducts: nextCategoryProducts,
          subcategoryProducts: nextSubcategoryProducts,
        },
      };
    });
    showWizardToast(t, "wizardProductsLinked", { count: uniqueIds.length });
  };

  const unassignProducts = (productIds) => {
    const uniqueIds = [...new Set((productIds || []).filter(Boolean))];
    if (!uniqueIds.length) return;

    setDraft((prev) => {
      const rel = prev.relations || {};
      let nextCategoryProducts = { ...(rel.categoryProducts || {}) };
      let nextSubcategoryProducts = { ...(rel.subcategoryProducts || {}) };

      uniqueIds.forEach((productId) => {
        const cleaned = removeProductFromAllMaps(
          productId,
          nextCategoryProducts,
          nextSubcategoryProducts
        );
        nextCategoryProducts = cleaned.nextCategoryProducts;
        nextSubcategoryProducts = cleaned.nextSubcategoryProducts;
      });

      return {
        ...prev,
        relations: {
          ...rel,
          categoryProducts: nextCategoryProducts,
          subcategoryProducts: nextSubcategoryProducts,
        },
      };
    });
    showWizardToast(t, "wizardProductsUnlinked", { count: uniqueIds.length });
  };

  const getDragIdsForProduct = (productId) => {
    if (selectedIds.has(productId) && selectedIds.size > 0) {
      return [...selectedIds];
    }
    return [productId];
  };

  const handleDragStart = (e, productId) => {
    dragMovedRef.current = false;
    const ids = getDragIdsForProduct(productId);
    setDraggingIds(ids);
    e.dataTransfer.setData("application/json", JSON.stringify({ productIds: ids }));
    e.dataTransfer.effectAllowed = "move";
    autoScroll.onDragStart();
  };

  const handleDragEnd = () => {
    autoScroll.onDragEnd();
    setDraggingIds([]);
    setDropHighlight(null);
    window.setTimeout(() => {
      dragMovedRef.current = false;
    }, 0);
  };

  const handleDropOnTarget = (e, targetType, targetId) => {
    e.preventDefault();
    e.stopPropagation();
    const fromPayload = getPayloadProductIds(e);
    const ids = fromPayload.length ? fromPayload : draggingIds;
    if (!ids.length) return;
    assignProductsBatch(ids, targetType, targetId);
    setSelectedIds(new Set());
    handleDragEnd();
  };

  const handleDropOnPool = (e) => {
    e.preventDefault();
    const fromPayload = getPayloadProductIds(e);
    const ids = fromPayload.length ? fromPayload : draggingIds;
    if (!ids.length) return;
    unassignProducts(ids);
    setSelectedIds(new Set());
    handleDragEnd();
  };

  const toggleProductSelection = (productId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const selectAllUnassigned = () => {
    setSelectedIds(new Set(unassignedProducts.map((p) => p.tempId)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const unassignSelectedProducts = (productIds) => {
    const uniqueIds = [...new Set((productIds || []).filter(Boolean))];
    if (!uniqueIds.length) return;
    unassignProducts(uniqueIds);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      uniqueIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const unassignAllSelectedAssigned = () => {
    const ids = [...selectedIds].filter((id) => assignedIds.has(id));
    unassignSelectedProducts(ids);
  };

  const toggleCategory = (categoryTempId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryTempId]: !prev[categoryTempId],
    }));
  };

  const expandAllCategories = () => {
    setExpandedCategories(
      Object.fromEntries(categoriesOrdered.map((c) => [c.tempId, true]))
    );
  };

  const collapseAllCategories = () => {
    setExpandedCategories(
      Object.fromEntries(categoriesOrdered.map((c) => [c.tempId, false]))
    );
  };

  const bindDrop = (key, onDrop) => ({
    onDragOver: (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "move";
      if (dropHighlight !== key) setDropHighlight(key);
    },
    onDragLeave: (event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        setDropHighlight((current) => (current === key ? null : current));
      }
    },
    onDrop: (event) => {
      event.preventDefault();
      event.stopPropagation();
      onDrop(event);
    },
  });

  const renderPoolProduct = (product) => {
    const isSelected = selectedIds.has(product.tempId);
    const isDragging = draggingIds.includes(product.tempId);
    const thumb = productPhotos?.[product.tempId] || null;

    return (
      <button
        key={product.tempId}
        type="button"
        draggable
        className={`start-link-product${isSelected ? " is-selected" : ""}${
          isDragging ? " is-dragging" : ""
        }`}
        onDragStart={(e) => handleDragStart(e, product.tempId)}
        onDrag={(e) => {
          if (e.clientX !== 0 || e.clientY !== 0) dragMovedRef.current = true;
        }}
        onDragEnd={handleDragEnd}
        onClick={() => {
          if (dragMovedRef.current) {
            dragMovedRef.current = false;
            return;
          }
          toggleProductSelection(product.tempId);
        }}
      >
        <span className={`start-link-product__check${isSelected ? " is-on" : ""}`} aria-hidden="true">
          {isSelected ? <i className="fa fa-check" /> : null}
        </span>
        {thumb ? (
          <img src={thumb} alt="" className="start-link-product__thumb" />
        ) : (
          <span className="start-link-product__thumb is-empty" aria-hidden="true">
            <i className="fa fa-utensils" />
          </span>
        )}
        <span className="start-link-product__copy">
          <span className="start-link-product__title">
            {getProductDisplayTitle(product, menuPrimaryLanguage)}
          </span>
          <span className="start-link-product__desc">
            {formatDualMoney(product.price ?? 0, product.secondaryPrice)}
          </span>
        </span>
        <i className="fa fa-grip-vertical start-link-product__grip" aria-hidden="true" />
      </button>
    );
  };

  const renderAssignedChip = (product) => {
    const isSelected = selectedIds.has(product.tempId);
    const isDragging = draggingIds.includes(product.tempId);

    return (
      <span
        key={product.tempId}
        className={`start-link-chip${isSelected ? " is-selected" : ""}${isDragging ? " is-dragging" : ""}`}
      >
        <button
          type="button"
          draggable
          className="start-link-chip__drag"
          onDragStart={(e) => handleDragStart(e, product.tempId)}
          onDrag={(e) => {
            if (e.clientX !== 0 || e.clientY !== 0) dragMovedRef.current = true;
          }}
          onDragEnd={handleDragEnd}
          onClick={() => {
            if (dragMovedRef.current) {
              dragMovedRef.current = false;
              return;
            }
            toggleProductSelection(product.tempId);
          }}
        >
          <i className="fa fa-grip-vertical" aria-hidden="true" />
          <span>{getProductDisplayTitle(product, menuPrimaryLanguage)}</span>
        </button>
        <button
          type="button"
          className="start-link-chip__remove"
          onClick={(e) => {
            e.stopPropagation();
            unassignSelectedProducts([product.tempId]);
          }}
          aria-label={t("remove")}
          title={t("remove")}
        >
          <i className="fa fa-times" aria-hidden="true" />
        </button>
      </span>
    );
  };

  const renderAssignedList = (productIds) => {
    if (!productIds.length) {
      return <p className="start-link-assigned-empty">{t("wizardDropProductsHere")}</p>;
    }

    return (
      <div className="start-link-assigned-list">
        {productIds.map((productId) => {
          const product = products.find((p) => p.tempId === productId);
          if (!product) return null;
          return renderAssignedChip(product);
        })}
      </div>
    );
  };

  const canNext = products.length === 0 || assignedIds.size > 0;
  const selectedCount = selectedIds.size;
  const selectedAssignedCount = [...selectedIds].filter((id) => assignedIds.has(id)).length;
  const allExpanded =
    categoriesOrdered.length > 0 &&
    categoriesOrdered.every((category) => expandedCategories[category.tempId]);

  return (
    <>
      <div className="mt-3 start-step-content" ref={autoScroll.rootRef}>
        <div className="start-step-hero">
          <h2 className="start-step-title">
            <span className="step-badge">7</span>
            {t("wizardLinkProductsHeading")}
          </h2>
          <p className="start-step-subtitle">{t("wizardLinkProductsSub")}</p>
        </div>

        <div className="start-options start-link-layout">
          <section
            className={`start-products-form start-link-col${
              dropHighlight === "pool" ? " is-drop-active" : ""
            }`}
            aria-label={t("wizardUnassignedProducts")}
            {...bindDrop("pool", handleDropOnPool)}
          >
            <div className="start-options-section-head">
              <span className="start-options-section-head__index">1</span>
              <h3 className="start-products-added__title">{t("wizardUnassignedProducts")}</h3>
              <span className="start-products-added__count">{unassignedProducts.length}</span>
              {unassignedProducts.length > 0 ? (
                <div className="start-link-head-actions">
                  <button type="button" className="start-link-text-btn" onClick={selectAllUnassigned}>
                    {t("wizardLinkSelectAll")}
                  </button>
                  {selectedCount > 0 ? (
                    <button type="button" className="start-link-text-btn" onClick={clearSelection}>
                      {t("wizardLinkClearSelection")}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            {selectedCount > 0 ? (
              <p className="start-assign-banner">{t("wizardLinkSelectedBanner", { count: selectedCount })}</p>
            ) : (
              <p className="start-options-hint">{t("wizardLinkProductsDragHint")}</p>
            )}

            <div className="start-link-col-body">
              {products.length === 0 ? (
                <p className="start-products-empty">{t("wizardNoProductsToLink")}</p>
              ) : unassignedProducts.length === 0 ? (
                <div className="start-link-done">{t("wizardAllProductsLinked")}</div>
              ) : (
                <div className="start-link-product-list">
                  {unassignedProducts.map((product) => renderPoolProduct(product))}
                </div>
              )}
            </div>
          </section>

          <section
            className="start-products-added start-link-col is-open"
            aria-label={t("wizardLinkCategoriesTitle")}
          >
            <div className="start-options-section-head">
              <span className="start-options-section-head__index">2</span>
              <h3 className="start-products-added__title">{t("wizardLinkCategoriesTitle")}</h3>
              <span className="start-products-added__count">{categoriesOrdered.length}</span>
              {categoriesOrdered.length > 0 ? (
                <div className="start-link-head-actions">
                  {selectedAssignedCount > 0 ? (
                    <button
                      type="button"
                      className="start-link-text-btn is-danger"
                      onClick={unassignAllSelectedAssigned}
                    >
                      {t("wizardLinkUnassignSelected", { count: selectedAssignedCount })}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="start-link-text-btn"
                    onClick={allExpanded ? collapseAllCategories : expandAllCategories}
                  >
                    {allExpanded ? t("wizardLinkCollapseAll") : t("wizardLinkExpandAll")}
                  </button>
                </div>
              ) : null}
            </div>

            <p className="start-options-hint">{t("wizardLinkCategoriesHint")}</p>

            <div className="start-link-col-body">
              {categoriesOrdered.length === 0 ? (
                <p className="start-products-empty">{t("wizardPreviewNoCategories")}</p>
              ) : (
                <div className="start-link-targets">
                  {categoriesOrdered.map((category) => {
                    const categoryTempId = category.tempId;
                    const dropKey = `category:${categoryTempId}`;
                    const isExpanded = !!expandedCategories[categoryTempId];
                    const isActive = dropHighlight === dropKey;
                    const subcategories = getSubcategoriesForCategory(categoryTempId);
                    const categoryProductIds = relations.categoryProducts?.[categoryTempId] || [];
                    const totalInCategory = countProductsInCategory(categoryTempId);

                    return (
                      <article
                        key={categoryTempId}
                        className={`start-link-cat${isExpanded ? " is-expanded" : ""}${
                          isActive ? " is-drop-active" : ""
                        }`}
                        {...bindDrop(dropKey, (event) =>
                          handleDropOnTarget(event, "category", categoryTempId)
                        )}
                      >
                        <div className="start-link-cat__header">
                          <button
                            type="button"
                            className="start-link-cat__toggle"
                            onClick={() => toggleCategory(categoryTempId)}
                            aria-expanded={isExpanded}
                          >
                            <span className="start-link-cat__copy">
                              <span className="start-link-cat__title">
                                {getCategoryDisplayTitle(category, menuPrimaryLanguage)}
                              </span>
                              <span className="start-link-cat__desc">
                                {[
                                  t("wizardLinkCategoryProductCount", { count: totalInCategory }),
                                  subcategories.length > 0
                                    ? t("wizardLinkSubcategoryCount", { count: subcategories.length })
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </span>
                            </span>
                            <span className="start-products-added__count">{totalInCategory}</span>
                            {isActive && draggingIds.length > 1 ? (
                              <span className="start-link-drop-count">
                                {t("wizardLinkDropCount", { count: draggingIds.length })}
                              </span>
                            ) : null}
                            <span
                              className={`start-link-cat__chevron${isExpanded ? " is-open" : ""}`}
                              aria-hidden="true"
                            >
                              <i className="fa fa-chevron-down" />
                            </span>
                          </button>
                        </div>

                        {isExpanded ? (
                          <div className="start-link-cat__body">
                            {subcategories.length > 0 ? (
                              <p className="start-link-zone-label">{t("wizardLinkInCategory")}</p>
                            ) : null}
                            {renderAssignedList(categoryProductIds)}

                            {subcategories.length > 0 ? (
                              <div className="start-link-subs">
                                <p className="start-link-zone-label">{t("wizardLinkSubcategoriesLabel")}</p>
                                {subcategories.map((subcategory) => {
                                  const subDropKey = `subcategory:${subcategory.tempId}`;
                                  const subProductIds =
                                    relations.subcategoryProducts?.[subcategory.tempId] || [];
                                  const subActive = dropHighlight === subDropKey;

                                  return (
                                    <div
                                      key={subcategory.tempId}
                                      className={`start-link-sub${subActive ? " is-drop-active" : ""}`}
                                      {...bindDrop(subDropKey, (event) =>
                                        handleDropOnTarget(event, "subcategory", subcategory.tempId)
                                      )}
                                    >
                                      <div className="start-link-sub__head">
                                        <span className="start-link-sub__title">
                                          {getSubcategoryDisplayTitle(subcategory, menuPrimaryLanguage)}
                                        </span>
                                        <span className="start-products-added__count">
                                          {subProductIds.length}
                                        </span>
                                        {subActive && draggingIds.length > 1 ? (
                                          <span className="start-link-drop-count">
                                            {t("wizardLinkDropCount", { count: draggingIds.length })}
                                          </span>
                                        ) : null}
                                      </div>
                                      {renderAssignedList(subProductIds)}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      <div className="toolbar toolbar-bottom start-toolbar">
        <button className="btn btn-secondary sw-btn-prev me-1 start-btn-back" onClick={goPrev} type="button">
          ← {t("back")}
        </button>
        <div className="wizard-step-counter">{t("wizardStep7of9")}</div>
        <button
          className="btn btn-primary sw-btn-next ms-1 start-btn-next"
          disabled={!canNext}
          onClick={goNext}
          type="button"
        >
          {t("wizardContinue")} <span aria-hidden="true">→</span>
        </button>
      </div>
    </>
  );
};

export default Step4LinkProducts;
